// import { createApi } from '@reduxjs/toolkit/query/react';
import {
  type BaseQueryApi,
  createApi,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { TokenStore } from '@repo/utils';
import * as Sentry from '@sentry/react';
import { Mutex } from 'async-mutex';

export const GLOBAL_TAG_TYPES = {
  session: 'Session',
  resetPassword: 'ResetPassword',
  listFilter: 'ListFilter',
  list: 'List',
  leadActivityList: 'leadActivityList',
  leadActivitySummaries: 'leadActivitySummaries',
  leadAttributes: 'LeadAttributes',
  leadActivityAttributes: 'LeadActivityAttributes',
  leadActivities: 'leadActivities',
  taskTypes: 'taskTypes',
  salesGroups: 'salesGroups',
  salesGroupUsers: 'salesGroupUsers',
  emailTempalte: 'emailTempalte',
  taskList: 'taskList',
  referencedProperties: 'referencedProperties',
  leadNotes: 'leadNotes',
  permissions: 'permissions',
  manageLeadActivities: 'manageLeadActivities',
  leadAndActivityAttributes: 'leadAndActivityAttributes',
  criteriaGroups: 'criteriaGroups',
  automationTree: 'automationTree',
  automationDetail: 'automationDetail',
  automationList: 'automationList',
  userAttendance: 'userAttendance',
  auditTrails: 'auditTrails',
  requestList: 'requestList',
  permissionTemplateList: 'permissionTemplateList',
  smartViewDashboard: 'smartViewDashboard',
  usersList: 'usersList',
  userPermissions: 'userPermissions',
  leadFields: 'leadFields',
  customActivities: 'customActivities',
  leadAttributeOptions: 'leadAttributeOptions',
  recentLeadChatMessagesForUser: 'recentLeadChatMessagesForUser',
  activityAttributeOptions: 'activityAttributeOptions',
  leadStages: 'leadStages',
  leadStageSchema: 'leadStageSchema',
  deals: 'deals',
  products: 'products',
  dealStatuses: 'dealStatuses',
  dealTransitions: 'dealTransitions',
  emailTemplates: 'emailTemplates',
  formsList: 'formsList',
  formTabs: 'formTabs',
  formSections: 'formSections',
  formInputFields: 'formInputFields',
  userFeatures: 'userFeatures',
  virtualNumbers: 'virtualNumbers',
};

export function customParamsSerializer(
  params: Record<string, string | number | boolean>,
): string {
  const cleanedParams: Record<string, string | number | boolean> = {};

  Object.keys(params).forEach((key) => {
    if (
      Object.prototype.hasOwnProperty.call(params, key)
      && params[key] !== null
      && params[key] !== undefined
    ) {
      cleanedParams[key] = params[key];
    }
  });

  const finalParams = Object.entries(cleanedParams).map(([key, value]) => {
    if (Array.isArray(value)) {
      return value.map((v) => `${key}[]=${encodeURIComponent(v)}`).join('&');
    } else {
      return `${key}=${encodeURIComponent(value)}`;
    }
  }).join('&');

  return finalParams;
}

export const baseQuery = fetchBaseQuery({
  baseUrl: '/core/',
  paramsSerializer: customParamsSerializer,
  prepareHeaders: (headers) => {
    const jwt = TokenStore.getToken();
    if (jwt) {
      headers.set('Authorization', `Bearer ${jwt}`);
    }
    return headers;
  },
});

/**
 * This function is used to track the status of the API call and capture the error if the status is > 500
 * @returns The result of the API call
 */
const queryWithStatusTracking = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions = {}) => {
  const result = await baseQuery(args, api, extraOptions);

  // capture all errors with status > 500
  if (result.meta?.response?.status && result.meta?.response?.status >= 500) {
    const request = result?.meta?.request as FetchArgs;
    const response = result?.meta?.response as Response;

    Sentry.captureMessage(`Status: ${response?.status} - API ${request?.url ?? '-'}`, {
      level: 'error',
      extra: {
        url: request?.url ?? '-',
        method: request?.method ?? '-',
        status: response?.status ?? '-',
        error: result?.error,
        data: result?.data,
        request,
        response,
      },
    });
  }

  return result;
};

const mutex = new Mutex();

/**
 * Endpoints that are safe to fetch without authentication. Tenant branding is
 * resolved by subdomain on the backend, so it must load on unauthenticated
 * pages like /login — otherwise branding falls back to defaults until the user
 * has logged in once and cached it.
 */
const PUBLIC_ENDPOINTS = ['/v1/tenants/info'];

const isPublicEndpoint = (args: string | FetchArgs): boolean => {
  const url = typeof args === 'string' ? args : args?.url;
  return url ? PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint)) : false;
};

export const baseQueryWithReAuth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions = {},
) => {
  // Public endpoints must work pre-auth, so bypass the token gate and the
  // reauth/redirect flow entirely.
  if (isPublicEndpoint(args)) {
    return queryWithStatusTracking(args, api, extraOptions);
  }

  const token = TokenStore.getToken();
  let result;

  if (token) {
    result = await queryWithStatusTracking(args, api, extraOptions);
  } else {
    result = { error: { status: 'FETCH_ERROR', error: 'No token available' } as FetchBaseQueryError, meta: {} };
  }
  const error = result?.error;

  if (!token || (error && ('originalStatus' in error && error.originalStatus === 401))) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        // If no access token, attempt to refresh it
        const refreshResult = await queryWithStatusTracking(
          {
            url: '/refresh', // Replace with your actual refresh endpoint
            method: 'GET',
          },
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          // Update the access token with the new one from refresh
          const accessToken = (refreshResult.data as { token: string }).token;

          TokenStore.setToken(accessToken);
          result = await queryWithStatusTracking(args, api, extraOptions);
        } else {
          // Handle refresh failure (e.g., log out the user)
          const { pathname } = window.location;
          const isAuthPage = ['/login', '/forgot-password', '/reset-password']
            .some((path) => pathname.includes(path));

          if (!isAuthPage) {
            const currentPath = `${pathname}${window.location.search}`;
            sessionStorage.setItem('redirectAfterLogin', currentPath);
            window.location.href = '/login/';
          }
          return refreshResult;
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    }
  }

  // Proceed with the original query if token exists or was successfully refreshed
  return result;
};

export const baseService = createApi({
  reducerPath: 'baseApiService',
  tagTypes: Object.values(GLOBAL_TAG_TYPES),
  async baseQuery(...args) {
    const response = await baseQueryWithReAuth(...args);
    return response;
  },
  endpoints: () => ({}),
});
